import type { BaseItemData, CatalogItemData, ItemByModule, ItemDetails, ItemDTO, ModuleType, NonEmptyArray } from "./item.type";
import type { GiftcardItem, GiftcardType } from "./subitems/giftcards.types";
import type { DiscountsItem } from "./subitems/discounts.types";
import type { PointsExchangeItem } from "./subitems/px.types";
import { REDEMPTION_CHANNELS, type RedemptionChannel } from "./item.enum";

class ItemTransformer {
  private static toBase(item: ItemDTO): BaseItemData {
    return {
      code: item.code,
      name: item.name,
      brand: item.brand,
      description: item.description,
      termsAndConditions: item.termsAndConditions,
      usageInstructions: item.usageInstructions,
      totalPrice: item.totalPrice,
      imageUrl: item.imageUrl ?? "",
      vat: item.vat,
      fees: item.fees,
    };
  }

  private static toCatalog(item: ItemDTO): CatalogItemData {
    return {
      categoriesList: item.categoriesList ?? [],
      categories: item.categories ?? [],
      catalogs: item.catalogs ?? [],
      tags: item.tags ?? [],
    };
  }

  private static toGiftcardType(item: ItemDTO): GiftcardType {
    return item.denominationType?.toLowerCase() === "range"
      ? { isRange: true, pointsFactor: item.pointsFactor ?? 1, min: Number(item.from ?? 0), max: Number(item.to ?? 0) }
      : { isRange: false, points: item.points ?? 0, value: Number(item.from ?? 0) };
  }

  private static toRedemptionChannel(channel: string | null): RedemptionChannel | null {
    const normalized = channel?.replace(/&/g, "and").replace(/[^a-z]/gi, "").toLowerCase();
    return Object.values(REDEMPTION_CHANNELS).find((value) => value.toLowerCase() === normalized) ?? null;
  }

  static toGiftcard(item: ItemDTO): GiftcardItem {
    return {
      ...ItemTransformer.toBase(item),
      ...ItemTransformer.toCatalog(item),
      moduleType: "GiftCards",
      currency: item.currency,
      redemptionChannel: ItemTransformer.toRedemptionChannel(item.redemptionChannel),
      validityInDays: item.validityInDays ?? 0,
      ...ItemTransformer.toGiftcardType(item),
    };
  }

  static toDiscount(item: ItemDTO): DiscountsItem {
    return {
      ...ItemTransformer.toBase(item),
      ...ItemTransformer.toCatalog(item),
      discount: item.discount ?? "",
      moduleType: "Discounts",
      currency: item.currency,
      redemptionChannel: ItemTransformer.toRedemptionChannel(item.redemptionChannel),
      isPromo: item.isPromo ?? false,
      points: item.points ?? 0,
    };
  }

  static toPointsExchange(item: ItemDTO): PointsExchangeItem {
    const { usageInstructions, ...base } = ItemTransformer.toBase(item);
    return {
      ...base,
      from: Number(item.from ?? 0),
      to: Number(item.to ?? 0),
      moduleType: "Points Exchange",
      conversionRate: item.conversionRate ?? 0,
      unitLabel: item.unitLabel ?? null,
      requiredFields: item.requiredFields ?? [],
    };
  }

  private static readonly transformers: { [M in ModuleType]: (item: ItemDTO) => ItemByModule[M] } = {
    GiftCards: ItemTransformer.toGiftcard,
    Discounts: ItemTransformer.toDiscount,
    "Points Exchange": ItemTransformer.toPointsExchange,
  };

  static toItem<M extends ModuleType>(moduleType: M, item: ItemDTO): ItemByModule[M] {
    return ItemTransformer.transformers[moduleType](item);
  }

  static toDetails<M extends ModuleType>(moduleType: M, [first, ...rest]: NonEmptyArray<ItemDTO>): ItemDetails<ItemByModule[M]> {
    return {
      brand: first.brand ?? "",
      imageUrl: first.imageUrl ?? "",
      items: [ItemTransformer.toItem(moduleType, first), ...rest.map((item) => ItemTransformer.toItem(moduleType, item))],
    };
  }
}

export default ItemTransformer;
